import React, { useEffect, useState, useRef, useCallback } from 'react';
import InfoTip from '../components/InfoTip';
import { clampMonth, currentMonth, periodParam, shiftMonth as stepMonthValue, tabParam } from '../utils/urlParams';
import { useTabParam } from '../hooks/useTabParam';
import { PHONE_QUERY, useMediaQuery } from '../hooks/useMediaQuery';
import { useUrlState } from '../hooks/useUrlState';
import { createPortal } from 'react-dom';
import { RefreshCw, Users, TrendingUp, Mail, ChevronLeft, ChevronRight, Tag, Search, ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';
import { apiService } from '../services/api';
import SlidePanel from '../components/SlidePanel';
import PageHeader from '../components/PageHeader';
import { navLabel } from '../utils/navigation';
import MonthPicker from '../components/MonthPicker';
import DetailsToggle from '../components/DetailsToggle';
import { useDetails } from '../hooks/useDetails';
import DownloadMenu from '../components/DownloadMenu';
import TagDistributionView from '../components/TagDistributionView';
import { useAuth } from '../contexts/AuthContext';
import NotificationModal from '../components/NotificationModal';
import LogTypePopover, { tagColour } from '../components/LogTypePopover';
import { saveBlob, saveCsv, toCsv } from '../utils/download';
import SummaryStrip from '../components/worklogs/SummaryStrip';
import PersonPanel from '../components/worklogs/PersonPanel';
import { Bucket, Sort, SortKey, WorklogRow, gridLayout, hoursBucket, isWeekend, matchesQuery, monthStats, nextSort, sortRows, workingDaysOver } from '../components/worklogs/model';
import * as fmt from '../lib/format';
import { statusStyle } from '../utils/reportFormat';

// The day colours of the grid, which are also the filter for it.
const BUCKETS = [
  ['all', 'All days', null],
  ['none', 'No log', 'bg-gray-300'],
  ['lo', 'Under 5 h', 'bg-red-500'],
  ['ok', '5–8 h', 'bg-emerald-500'],
  ['hi', 'Over 8 h', 'bg-amber-400'],
] as const;

const GROUP_FILTERS = [
  { label: 'All',          dbGroup: ''                 },
  { label: 'Developer',    dbGroup: 'Developers'       },
  { label: 'QA Team',      dbGroup: 'QA Team'          },
  { label: 'DevOps',       dbGroup: 'DevOps'           },
  { label: 'Designer',     dbGroup: 'Designer'         },
  { label: 'Product Team', dbGroup: 'Product Managers' },
];

const DESIGNATION_LOG_TYPES: Record<string, string[]> = {
  'Developers': ['Dev', 'Debug', 'Code Review', 'Discussion', 'Research', 'DevOps', 'Hotfix', 'Documentation', 'Rework'],
  'DevOps':     ['Dev', 'Debug', 'Code Review', 'Discussion', 'Research', 'DevOps', 'Hotfix', 'Documentation', 'Rework'],
  'QA Team':    ['Testing', 'Bug Verification', 'Test Setup', 'Discussion'],
  'Designer':   ['Design', 'Research', 'Revision', 'Discussion'],
  'Product Managers': ['Discussion', 'Scoping', 'Review', 'Coordination'],
};

function getDesignationLogTypes(groups: string[]): string[] | null {
  for (const g of groups) {
    if (DESIGNATION_LOG_TYPES[g]) return DESIGNATION_LOG_TYPES[g];
  }
  return null;
}

const ReportsPage: React.FC = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const isLeader = user?.role === 'leader';

  const canSendNotification = isAdmin || isLeader;

  // Context menu state for Worklog summary table rows
  const [rptCtxMenu, setRptCtxMenu] = useState<{ x: number; y: number; user: { display_name: string | null; email_address: string | null } } | null>(null);
  const rptCtxMenuRef = useRef<HTMLDivElement>(null);
  const [rptNotifyTarget, setRptNotifyTarget] = useState<{ display_name: string | null; email_address: string | null } | null>(null);
  // accountId → email map (reuses userGroupsMap fetch)
  const [rptEmailMap, setRptEmailMap] = useState<Record<string, string>>({});

  const closeRptCtxMenu = useCallback(() => setRptCtxMenu(null), []);

  useEffect(() => {
    if (!rptCtxMenu) return;
    const handler = (e: MouseEvent) => {
      if (rptCtxMenuRef.current && !rptCtxMenuRef.current.contains(e.target as Node)) closeRptCtxMenu();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeRptCtxMenu();
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('keydown', onKey);
    };
  }, [rptCtxMenu, closeRptCtxMenu]);

  const openRptCtxMenu = (e: React.MouseEvent, displayName: string, accountId: string) => {
    if (!canSendNotification) return;
    e.preventDefault();
    const email = rptEmailMap[accountId] || null;
    const menuW = 210; const menuH = 52;
    const x = Math.min(e.clientX, window.innerWidth - menuW - 8);
    const y = Math.min(e.clientY, window.innerHeight - menuH - 8);
    setRptCtxMenu({ x, y, user: { display_name: displayName, email_address: email } });
  };
  // What the person chose lives in the address, so a reload or a copied link opens the same grid:
  // /reports?period=2026-09&group=QA%20Team&mode=avg&bucket=none&person=<account id>&view=tags
  const { params, update } = useUrlState();
  // On a phone only the name and the total stay pinned; the number, role and manager columns are left out so the days have room.
  const phone = useMediaQuery(PHONE_QUERY);
  const [activeTab, setActiveTab] = useTabParam<'user' | 'tags'>(['user', 'tags'], 'user', 'view');
  // The grid for the month. A new request (another month, Refresh, Try again) cancels the one before it,
  // so a slow answer never lands under the wrong month.
  const [userState, setUserState] = useState<
    { status: 'loading' } | { status: 'ready'; data: any } | { status: 'error'; message: string }
  >({ status: 'loading' });
  const [attempt, setAttempt] = useState(0);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const summary = userState.status === 'ready' ? userState.data : null;
  const loading = userState.status === 'loading';
  const groupFilter = tabParam(params, GROUP_FILTERS.map((g) => g.dbGroup).filter(Boolean), 'group') ?? '';
  const setGroupFilter = (group: string) => update({ group: group || null });
  const totalMode: 'total' | 'avg' = params.get('mode') === 'avg' ? 'avg' : 'total';
  // Headline first: the search stays; the group, the day colours and total/average are one press away (?details=1).
  const [filtersOpen, setFiltersOpen] = useDetails();
  const setTotalMode = (mode: 'total' | 'avg') => update({ mode: mode === 'avg' ? 'avg' : null });
  // Search, the legend as a filter, the order of the people, and whose month is open in the side panel.
  const [query, setQuery] = useState('');
  const bucketFilter: Bucket | 'all' = tabParam<Bucket>(params, ['none', 'lo', 'ok', 'hi'], 'bucket') ?? 'all';
  const setBucketFilter = (bucket: Bucket | 'all') => update({ bucket: bucket === 'all' ? null : bucket });
  const [sort, setSort] = useState<Sort | null>(null);
  const personInAddress = params.get('person');
  const personKey = personInAddress && /^[A-Za-z0-9:_-]{1,128}$/.test(personInAddress) ? personInAddress : null;
  const setPersonKey = (key: string | null) => update({ person: key });
  const gridRef = useRef<HTMLTableElement>(null);
  // accountId → groups[] map fetched from /db/users
  const [userGroupsMap, setUserGroupsMap] = useState<Record<string, string[]>>({});
  const [issuePanelOpen, setIssuePanelOpen] = useState(false);
  const [issuePanelLoading, setIssuePanelLoading] = useState(false);
  const [issuePanelTitle, setIssuePanelTitle] = useState('');
  const [issuePanelIssues, setIssuePanelIssues] = useState<any[]>([]);
  const [issuePanelError, setIssuePanelError] = useState<string | null>(null);
  const [issuePanelTotals, setIssuePanelTotals] = useState<{ seconds: number; count: number }>({ seconds: 0, count: 0 });
  const [expandedIssues, setExpandedIssues] = useState<Set<string>>(new Set());
  const [worklogDetails, setWorklogDetails] = useState<Record<string, any[]>>({});
  const [loadingWorklogs, setLoadingWorklogs] = useState<Set<string>>(new Set());
  const [currentAccountId, setCurrentAccountId] = useState<string>('');
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [activePanelLogType, setActivePanelLogType] = useState<string | null>(null);
  const [panelLogTypeBreakdown, setPanelLogTypeBreakdown] = useState<Array<{ log_type: string; minutes: number; hours: number }>>([]);
  // A link such as /reports?period=2026-09 opens that month (the Dashboard's Time logged card does). This month
  // is left out of the address; a later month reads as this one. Another month closes the person's panel.
  const currentMonthStr = currentMonth();
  const month = clampMonth(periodParam(params), currentMonthStr);
  const setMonth = (next: string) => update({ period: next >= currentMonthStr ? null : next, person: null });

  useEffect(() => {
    if (activeTab !== 'user') return;
    let cancelled = false;
    setUserState({ status: 'loading' });
    const [y, m] = month.split('-').map(Number);
    apiService
      .getUserMonthly(y, m)
      .then((res: any) => {
        if (cancelled) return;
        setUserState(res?.success ? { status: 'ready', data: res.data } : { status: 'error', message: res?.error || 'Could not load the worklogs' });
      })
      .catch((e: any) => {
        if (!cancelled) setUserState({ status: 'error', message: e?.response?.data?.error || e?.message || 'Could not load the worklogs' });
      });
    return () => {
      cancelled = true;
    };
  }, [activeTab, month, attempt]);

  // Which group each person is in, for the filter. Not critical: without it the filter just finds no one.
  useEffect(() => {
    if (activeTab !== 'user') return;
    let cancelled = false;
    apiService
      .getJiraUsers()
      .then((res: any) => {
        if (cancelled || !res.data?.success) return;
        const map: Record<string, string[]> = {};
        const emailMap: Record<string, string> = {};
        (res.data.data || []).forEach((u: any) => {
          map[u.account_id] = Array.isArray(u.groups) ? u.groups : [];
          if (u.account_id && u.email_address) emailMap[u.account_id] = u.email_address;
        });
        setUserGroupsMap(map);
        setRptEmailMap(emailMap);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [activeTab]);

  const refresh = () => {
    setDownloadError(null);
    setAttempt((a) => a + 1);
  };

  // Step the selected month by a number of months (e.g. -1 prev, +1 next).
  const shiftMonth = (delta: number) => setMonth(stepMonthValue(month, delta));

  const toggleWorklogDetails = (issueId: string) => {
    const isExpanded = expandedIssues.has(issueId);
    
    if (isExpanded) {
      // Collapse - remove from expanded set
      setExpandedIssues(prev => {
        const newSet = new Set(prev);
        newSet.delete(issueId);
        return newSet;
      });
    } else {
      // Expand - add to expanded set immediately for smooth UI
      setExpandedIssues(prev => new Set(prev).add(issueId));
      
      // Only fetch if we don't already have the data
      if (!worklogDetails[issueId] || worklogDetails[issueId].length === 0) {
        if (!currentAccountId) {
          console.error('No currentAccountId available for worklog details');
          return;
        }
        if (!selectedDate) {
          console.error('No selectedDate available for worklog details');
          return;
        }
        
        // Fetch worklog details asynchronously without blocking UI
        const fetchWorklogDetails = async () => {
          try {
            setLoadingWorklogs(prev => new Set(prev).add(issueId));
            const res = await apiService.getWorklogDetails(issueId, currentAccountId || undefined, selectedDate || undefined);
            
            if (res.data?.success) {
              setWorklogDetails(prev => ({
                ...prev,
                [issueId]: res.data.data
              }));
            } else {
              console.error('Failed to load worklog details:', res.data);
            }
          } catch (error) {
            console.error('Error loading worklog details:', error);
          } finally {
            setLoadingWorklogs(prev => {
              const newSet = new Set(prev);
              newSet.delete(issueId);
              return newSet;
            });
          }
        };
        
        fetchWorklogDetails();
      }
    }
  };

  // Auto-collapse worklog details when issue panel closes or account changes
  const collapseAllWorklogDetails = () => {
    setExpandedIssues(new Set());
    setWorklogDetails({});
    setLoadingWorklogs(new Set());
  };

  const errorOf = (e: any, fallback: string) => e?.response?.data?.error || e?.message || fallback;

  const openIssuePanel = async (accountId: string, date: string, userName: string, logType?: string) => {
    const isNewCell = accountId !== currentAccountId || date !== selectedDate;
    try {
      setIssuePanelOpen(true);
      setIssuePanelLoading(true);
      setIssuePanelError(null);
      setIssuePanelIssues([]);
      setActivePanelLogType(logType ?? null);
      // The date is a calendar day ("2026-10-05"): label it from the text so a time zone cannot move it.
      const dateLabel = fmt.dayMonth(date);
      setIssuePanelTitle(`${userName} · ${dateLabel}`);
      setCurrentAccountId(accountId);
      setSelectedDate(date);
      if (isNewCell) {
        setPanelLogTypeBreakdown([]);
        const groups = userGroupsMap[accountId] || [];
        const designationTypes = getDesignationLogTypes(groups);
        apiService.getUserDayLogType(accountId, date).then(res => {
          if (res?.success && Array.isArray(res.data)) {
            const actualMap = new Map<string, { log_type: string; minutes: number; hours: number }>(
              res.data.map((item: any) => [item.log_type, item])
            );
            if (designationTypes) {
              const merged = designationTypes.map(lt =>
                actualMap.get(lt) ?? { log_type: lt, minutes: 0, hours: 0 }
              );
              // Append any actual log types not in the designation list (e.g. new tags like Automation)
              const known = new Set(designationTypes);
              for (const item of res.data as Array<{ log_type: string; minutes: number; hours: number }>) {
                if (item.log_type !== '[]' && !known.has(item.log_type)) merged.push(item);
              }
              // Append untagged [] if present in actual data
              if (actualMap.has('[]')) merged.push(actualMap.get('[]')!);
              setPanelLogTypeBreakdown(merged);
            } else {
              setPanelLogTypeBreakdown(res.data);
            }
          }
        }).catch(() => {});
      }
      const res = await apiService.getUserDayIssues(accountId, date, logType);
      if (res.success) {
        setIssuePanelIssues(res.data.issues || []);
        setIssuePanelTotals({ seconds: res.data.totalSeconds || 0, count: res.data.totalWorklogs || 0 });
      } else {
        setIssuePanelError(res.error || 'Could not load the issues');
      }
    } catch (e: any) {
      setIssuePanelError(errorOf(e, 'Could not load the issues'));
    } finally {
      setIssuePanelLoading(false);
    }
  };

  const handlePillClick = async (logType: string) => {
    const next = logType === activePanelLogType ? null : logType;
    setActivePanelLogType(next);
    setIssuePanelLoading(true);
    setIssuePanelError(null);
    setIssuePanelIssues([]);
    try {
      const res = await apiService.getUserDayIssues(currentAccountId, selectedDate, next ?? undefined);
      if (res.success) {
        setIssuePanelIssues(res.data.issues || []);
        setIssuePanelTotals({ seconds: res.data.totalSeconds || 0, count: res.data.totalWorklogs || 0 });
      } else {
        setIssuePanelError(res.error || 'Could not load the issues');
      }
    } catch (e: any) {
      setIssuePanelError(errorOf(e, 'Could not load the issues'));
    } finally {
      setIssuePanelLoading(false);
    }
  };

  // The people on screen: the grid narrowed by the group filter. The CSV and the Excel file follow it.
  const groupRows: any[] = summary
    ? summary.rows.filter((r: any) => !groupFilter || (userGroupsMap[r.accountId] || []).includes(groupFilter))
    : [];
  const groupLabel = GROUP_FILTERS.find((g) => g.dbGroup === groupFilter)?.label ?? 'All';
  // What narrows or changes the grid, said in words, for when the filters are closed.
  const activeFilters = [
    groupFilter ? groupLabel : null,
    bucketFilter !== 'all' ? BUCKETS.find(([k]) => k === bucketFilter)?.[1] ?? null : null,
    totalMode === 'avg' ? 'Average per day logged' : null,
  ].filter((x): x is string => !!x);

  // What is on screen: the group, narrowed by the search, in the order asked for.
  const visibleRows: WorklogRow[] = sortRows(groupRows.filter((r: WorklogRow) => matchesQuery(r, query)), sort);
  const gridDays: string[] = summary?.days ?? [];
  // The server says what today is (app timezone); an older answer without it ends at the last day shown.
  const today: string = summary?.today ?? gridDays[gridDays.length - 1] ?? '';
  const stats = monthStats(visibleRows, gridDays, today);
  const personRow: WorklogRow | null = personKey && summary ? summary.rows.find((r: any) => r.accountId === personKey) ?? null : null;

  const sortIcon = (key: SortKey) =>
    sort?.key !== key ? <ArrowUpDown className="h-3 w-3 opacity-50" aria-hidden /> : sort.dir === 1 ? <ArrowUp className="h-3 w-3" aria-hidden /> : <ArrowDown className="h-3 w-3" aria-hidden />;
  const ariaSort = (key: SortKey): 'ascending' | 'descending' | 'none' => (sort?.key === key ? (sort.dir === 1 ? 'ascending' : 'descending') : 'none');
  const totalKey: SortKey = totalMode === 'total' ? 'total' : 'average';
  const changeMode = (mode: 'total' | 'avg') => {
    setTotalMode(mode);
    setSort(null);
  };

  // The column under the pointer is lit along with the row. Done on the elements directly: the grid has
  // hundreds of cells and a React render on every move would make it stutter.
  const lightColumn = (e: React.MouseEvent<HTMLTableElement>) => {
    const table = gridRef.current;
    if (!table) return;
    const day = ((e.target as HTMLElement).closest('[data-day]') as HTMLElement | null)?.dataset.day ?? '';
    if (table.dataset.hot === day) return;
    table.querySelectorAll('.is-col-hover').forEach((el) => el.classList.remove('is-col-hover'));
    table.dataset.hot = day;
    if (day) table.querySelectorAll(`[data-day="${day}"]`).forEach((el) => el.classList.add('is-col-hover'));
  };
  const dimColumn = () => {
    const table = gridRef.current;
    if (!table) return;
    table.querySelectorAll('.is-col-hover').forEach((el) => el.classList.remove('is-col-hover'));
    table.dataset.hot = '';
  };

  const fileSuffix = groupFilter ? `-${groupLabel.toLowerCase().replace(/[^a-z0-9]+/g, '-')}` : '';

  const downloadCsv = () => {
    if (!summary) return;
    const header = ['User', 'Role', 'Total (h)', ...summary.days.map((d: string) => fmt.dayMonth(d))];
    const rows = visibleRows.map((r: any) => [
      r.userName,
      r.userRole || '',
      fmt.decimal(r.totalHours || 0),
      ...summary.days.map((d: string) => {
        const mins = r.dailyMinutes?.[d] || 0;
        return mins === 0 ? '' : fmt.decimal(mins / 60);
      }),
    ]);
    const [y, m] = month.split('-');
    saveCsv(toCsv(header, rows), `user-monthly-summary-${y}-${m}${fileSuffix}.csv`);
  };

  // The whole month as one real .xlsx from the server, limited to the group on screen.
  const downloadXlsx = async () => {
    const [y, m] = month.split('-').map(Number);
    setDownloadError(null);
    setDownloading(true);
    try {
      saveBlob(await apiService.downloadUserMonthly(y, m, groupFilter || undefined), `User-worklogs-${y}-${String(m).padStart(2, '0')}${fileSuffix}.xlsx`);
    } catch (e: any) {
      setDownloadError(errorOf(e, 'The download could not be created'));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <>
    <div role="presentation" className="min-h-screen bg-gray-50" onClick={collapseAllWorklogDetails}>
      <PageHeader
        title={navLabel('/reports')}
        subtitle="Per-user worklog reporting and analytics"
        icon={TrendingUp}
      />

      <div className="p-4 sm:p-6">
        <div className="max-w-7xl mx-auto">
        {/* Tabs */}
        <div className="mb-4 flex flex-wrap items-center gap-x-2 gap-y-2">
          <button
            onClick={() => setActiveTab('user')}
            className={`px-3 py-1 rounded-full text-sm transition-colors ${activeTab === 'user' ? 'bg-blue-100 text-blue-700' : 'text-gray-600 hover:bg-gray-100'}`}
          >
            <Users className="w-4 h-4 inline mr-1" />User
          </button>
          <button
            onClick={() => setActiveTab('tags')}
            className={`px-3 py-1 rounded-full text-sm transition-colors ${activeTab === 'tags' ? 'bg-blue-100 text-blue-700' : 'text-gray-600 hover:bg-gray-100'}`}
          >
            <Tag className="w-4 h-4 inline mr-1" />Tags
          </button>
          <InfoTip id="worklogs.tags" className="" />
          <div className="ml-auto flex items-center gap-1">
            <button
              onClick={() => shiftMonth(-1)}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
              title="Previous month"
              aria-label="Previous month"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <MonthPicker value={month} max={currentMonthStr} onChange={setMonth} />
            <button
              onClick={() => shiftMonth(1)}
              disabled={month >= currentMonthStr}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed"
              title="Next month"
              aria-label="Next month"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
          <button onClick={refresh} disabled={loading} className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg disabled:opacity-50" title="Refresh" aria-label="Refresh">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`}/>
          </button>
        </div>

        {/* Content */}
        {activeTab === 'tags' ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
          <TagDistributionView month={month} />
        </div>
        ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {fmt.month(month)} Worklog summary
              </h3>
              <DownloadMenu
                  busy={downloading}
                  disabled={!summary || groupRows.length === 0}
                  items={[
                    {
                      key: 'xlsx',
                      title: 'Whole month (.xlsx)',
                      hint: `A summary and the hours per person per day. Group: ${groupLabel}`,
                      onSelect: downloadXlsx,
                    },
                    {
                      key: 'csv',
                      title: 'As shown (.csv)',
                      hint: `${visibleRows.length} ${visibleRows.length === 1 ? 'person' : 'people'}, group: ${groupLabel}`,
                      onSelect: downloadCsv,
                    },
                  ]}
                />
            </div>

            {summary && summary.days && summary.rows && (
              <SummaryStrip
                stats={stats}
                allPeople={groupRows.length}
                noLogOn={bucketFilter === 'none'}
                onNoLog={() => setBucketFilter(bucketFilter === 'none' ? 'all' : 'none')}
              />
            )}

            {/* Filters: search a person, the group, and what the legend colours mean. For a normal user, only the
                groups present among their scoped rows (team members + themselves) are offered; admin/leader see all. */}
            <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-2">
              <label className="relative">
                <span className="sr-only">Search a person</span>
                <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-500" aria-hidden />
                <input
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search a person"
                  className="w-44 rounded-lg border border-gray-300 bg-white py-1.5 pl-8 pr-2 text-xs text-gray-800 placeholder:text-gray-500 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
                />
              </label>
              <DetailsToggle open={filtersOpen} onChange={setFiltersOpen} showLabel="Show filters" hideLabel="Hide filters" controls="worklog-filters" />
              {!filtersOpen && activeFilters.length > 0 && <p className="text-xs text-gray-600">Showing: {activeFilters.join(' · ')}</p>}
            </div>

            {filtersOpen && (
              <div id="worklog-filters" className="mb-4 flex flex-wrap items-center gap-x-4 gap-y-2">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs font-medium text-gray-500 shrink-0">Group:</span>
                {(() => {
                  const present = new Set<string>();
                  (summary?.rows || []).forEach((r: any) =>
                    (userGroupsMap[r.accountId] || []).forEach((g: string) => present.add(g))
                  );
                  const visibleFilters = (isAdmin || isLeader)
                    ? GROUP_FILTERS
                    : GROUP_FILTERS.filter(({ dbGroup }) => dbGroup === '' || present.has(dbGroup));
                  return visibleFilters.map(({ label, dbGroup }) => (
                    <button
                      key={label}
                      onClick={() => setGroupFilter(dbGroup)}
                      className={`px-3 py-1 text-xs rounded-full border transition-colors ${
                        groupFilter === dbGroup
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400 hover:text-blue-600'
                      }`}
                    >
                      {label}
                    </button>
                  ));
                })()}
                </div>
                <div className="ml-auto flex flex-wrap items-center gap-2" role="group" aria-label="Show days">
                  {BUCKETS.map(([key, label, dot]) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setBucketFilter(key)}
                      aria-pressed={bucketFilter === key}
                      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs transition-colors motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${
                        bucketFilter === key ? 'border-blue-300 bg-blue-50 text-blue-700' : 'border-gray-300 bg-white text-gray-600 hover:border-blue-400 hover:text-blue-600'
                      }`}
                    >
                      {dot && <span className={`h-2 w-2 rounded-full ${dot}`} aria-hidden />}
                      {label}
                      {key !== 'all' && <span className="tabular-nums text-gray-500">{stats.cells[key]}</span>}
                    </button>
                  ))}
                  <div className="inline-flex rounded-full bg-gray-100 p-0.5 text-xs" role="group" aria-label="Show the total or the average">
                    {([['total', 'Total'], ['avg', 'Average']] as const).map(([mode, label]) => (
                      <button
                        key={mode}
                        type="button"
                        onClick={() => changeMode(mode)}
                        aria-pressed={totalMode === mode}
                        title={mode === 'total' ? 'Hours in the month' : 'Hours on the days a person logged'}
                        className={`rounded-full px-2.5 py-1 transition-colors motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${
                          totalMode === mode ? 'bg-white font-medium text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {(userState.status === 'error' || downloadError) && (
              <div role="alert" className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                <span>
                  {downloadError
                    ? `The download could not be created: ${downloadError}`
                    : `The worklogs could not be loaded: ${userState.status === 'error' ? userState.message : ''}`}
                </span>
                <button
                  type="button"
                  onClick={refresh}
                  className="rounded-lg bg-white px-3 py-1 text-xs font-semibold text-rose-700 ring-1 ring-inset ring-rose-200 transition-colors hover:bg-rose-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-400"
                >
                  Try again
                </button>
              </div>
            )}

            {loading ? (
              <div className="py-10 text-center"><RefreshCw className="w-6 h-6 animate-spin text-gray-400 inline"/></div>
            ) : summary && summary.days && summary.rows ? (
              <div className="max-h-[70dvh] overflow-auto pb-4 sm:h-[calc(100vh-16rem)] sm:max-h-none">
                {(() => {
                  const filteredRows: any[] = visibleRows;
                  const overDays = new Set(workingDaysOver(summary.days, today));

                  // Column widths (must match colgroup exactly)
                  // Column widths (must match colgroup exactly) and where the frozen columns stick
                  const { widths: W, left, showMeta } = gridLayout(phone, isAdmin);

                  // Rigid width so declared === rendered (offsets stay exact). box-sizing:border-box keeps padding inside.
                  const fix = (w: number) => ({ width: w, minWidth: w, maxWidth: w, boxSizing: 'border-box' as const });

                  const S = {
                    hdr:  (l: number, w: number, top = 0) => ({ ...fix(w), position: 'sticky' as const, top, left: l, zIndex: 30, background: '#ffffff' }),
                    hdr2: (l: number, w: number)          => ({ ...fix(w), position: 'sticky' as const, top: 33, left: l, zIndex: 30, background: '#f9fafb' }),
                    body: (l: number, w: number)          => ({ ...fix(w), position: 'sticky' as const, left: l, zIndex: 10 }),
                    dayH: (top = 0)                        => ({ ...fix(W.day), position: 'sticky' as const, top, zIndex: 20, background: top === 0 ? '#ffffff' : '#f9fafb' }),
                    dayC: ()                               => fix(W.day),
                  };

                  const shadow = { boxShadow: '3px 0 6px -2px rgba(0,0,0,0.08)' };

                  return (
                <table ref={gridRef} onMouseOver={lightColumn} onMouseLeave={dimColumn} className="worklog-grid border-separate table-fixed" style={{ borderSpacing: 0, width: 'max-content' }}>
                  <colgroup>
                    {showMeta && <col style={{ width: W.sr }} />}
                    <col style={{ width: W.user }} />
                    {showMeta && <col style={{ width: W.role }} />}
                    {isAdmin && showMeta && <col style={{ width: W.manager }} />}
                    <col style={{ width: W.total }} />
                    {summary.days.map((_: string, i: number) => <col key={i} style={{ width: W.day }} />)}
                  </colgroup>

                  <thead>
                    {/* Row 1 — column headers */}
                    <tr>
                      {showMeta && <th style={S.hdr(left.sr, W.sr)}   className="p-2 text-center text-xs font-medium text-gray-500 whitespace-nowrap">#</th>}
                      <th style={S.hdr(left.user, W.user)} aria-sort={ariaSort('user')} className="p-2 text-left text-xs font-medium text-gray-500 whitespace-nowrap">
                        <button
                          type="button"
                          onClick={() => setSort(nextSort(sort, 'user'))}
                          title="Sort by name"
                          className="hit-area inline-flex items-center gap-1 rounded transition-colors hover:text-blue-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 motion-reduce:transition-none"
                        >
                          User {sortIcon('user')}
                        </button>
                      </th>
                      {showMeta && <th style={S.hdr(left.role, W.role)}  className="p-2 text-center text-xs font-medium text-gray-500 overflow-hidden">Role</th>}
                      {isAdmin && showMeta && (
                        <th style={S.hdr(left.manager, W.manager)} className="p-2 text-center text-xs font-medium text-gray-500 whitespace-nowrap">Jira manager</th>
                      )}
                      <th style={{ ...S.hdr(left.total, W.total), ...shadow }} aria-sort={ariaSort(totalKey)} className="p-2 text-center text-xs font-medium text-gray-500 whitespace-nowrap">
                        <button
                          type="button"
                          onClick={() => setSort(nextSort(sort, totalKey))}
                          title={totalMode === 'total' ? 'Sort by hours in the month' : 'Sort by the average'}
                          className="hit-area inline-flex items-center gap-1 rounded transition-colors hover:text-blue-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 motion-reduce:transition-none"
                        >
                          {totalMode === 'total' ? 'Total (h)' : 'Avg (h)'} {sortIcon(totalKey)}
                        </button>
                      </th>
                      {summary.days.map((d: string) => (
                        <th
                          key={d}
                          data-day={d}
                          title={fmt.weekdayDay(d)}
                          style={{ ...S.dayH(0), background: isWeekend(d) ? '#f3f4f6' : '#ffffff', ...(d === today ? { boxShadow: 'inset 0 -2px 0 #2563eb' } : {}) }}
                          className={`p-1 text-center text-xs whitespace-nowrap ${d === today ? 'font-semibold text-blue-700' : 'font-medium text-gray-600'}`}
                        >
                          {Number(d.slice(8, 10))}
                        </th>
                      ))}
                    </tr>

                    {/* Row 2 — totals summary */}
                    <tr className="font-semibold">
                      {showMeta && <th style={{ ...S.hdr2(left.sr, W.sr), background: '#f9fafb' }}   className="p-2 text-center text-sm text-gray-900">–</th>}
                      <th style={{ ...S.hdr2(left.user, W.user), background: '#f9fafb' }} className="p-2 text-sm text-gray-900 whitespace-nowrap">Total</th>
                      {showMeta && <th style={{ ...S.hdr2(left.role, W.role), background: '#f9fafb' }} className="p-2 text-center text-xs text-gray-600 overflow-hidden">–</th>}
                      {isAdmin && showMeta && (
                        <th style={{ ...S.hdr2(left.manager, W.manager), background: '#f9fafb' }} className="p-2 text-center text-xs text-gray-600">–</th>
                      )}
                      <th style={{ ...S.hdr2(left.total, W.total), ...shadow, background: '#f9fafb' }} className="p-2 text-center text-xs font-bold text-gray-900">
                        {totalMode === 'total'
                          ? fmt.hoursBare(filteredRows.reduce((s: number, r: any) => s + (r.totalHours || 0), 0))
                          : (() => {
                              const vals = filteredRows.map((r: any) => {
                                const daysWorked = summary.days.filter((d: string) => (r.dailyMinutes?.[d] || 0) > 0).length;
                                return daysWorked > 0 ? (r.totalHours || 0) / daysWorked : 0;
                              });
                              return fmt.hoursBare(vals.reduce((s: number, v: number) => s + v, 0) / (vals.length || 1));
                            })()
                        }
                      </th>
                      {summary.days.map((d: string) => {
                        const totalMins = filteredRows.reduce((s: number, r: any) => s + (r.dailyMinutes?.[d] || 0), 0);
                        return (
                          <th key={d} data-day={d} style={{ ...S.dayH(33), background: isWeekend(d) ? '#eceef1' : '#f9fafb' }} className="p-1 text-center text-xs font-bold text-gray-900">
                            {totalMins === 0 ? <span className="text-gray-300" aria-hidden>–</span> : fmt.hoursBare(totalMins / 60)}
                          </th>
                        );
                      })}
                    </tr>
                  </thead>

                  <tbody>
                    {filteredRows.map((r: any, index: number) => (
                      <tr key={r.userId} onContextMenu={(e) => openRptCtxMenu(e, r.userName, r.accountId)} className="group">
                        {showMeta && <td style={S.body(left.sr, W.sr)}      className="bg-white group-hover:bg-gray-50 border-t border-gray-100 p-2 text-center text-sm text-gray-500 whitespace-nowrap">{index + 1}</td>}
                        <td style={S.body(left.user, W.user)}    className="bg-white group-hover:bg-gray-50 border-t border-gray-100 p-2 text-sm text-gray-900 whitespace-nowrap">
                          <button
                            type="button"
                            onClick={() => setPersonKey(r.accountId)}
                            title="Open this person's month"
                            className="hit-area block max-w-full truncate rounded text-left font-medium transition-colors hover:text-blue-700 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 motion-reduce:transition-none"
                          >
                            {r.userName}
                          </button>
                        </td>
                        {showMeta && <td style={S.body(left.role, W.role)}    className="bg-white group-hover:bg-gray-50 border-t border-gray-100 p-2 text-xs text-center text-gray-600 overflow-hidden text-ellipsis whitespace-nowrap">{r.userRole || '-'}</td>}
                        {isAdmin && showMeta && (
                          <td style={S.body(left.manager, W.manager)} className="bg-white group-hover:bg-gray-50 border-t border-gray-100 p-2 text-xs text-center text-gray-700 whitespace-nowrap">
                            {r.managerName || <span className="text-gray-300" aria-hidden>–</span>}
                          </td>
                        )}
                        <td style={{ ...S.body(left.total, W.total), ...shadow }} className={`bg-white group-hover:bg-gray-50 border-t border-gray-100 p-1 text-xs text-center font-medium ${(r.totalHours || 0) < 5 ? 'text-red-600' : 'text-emerald-700'}`}>
                          {totalMode === 'total' ? (
                            <LogTypePopover
                              accountId={r.accountId}
                              year={Number(month.split('-')[0])}
                              month={Number(month.split('-')[1])}
                              onLogTypeClick={() => {}}
                              designationTypes={getDesignationLogTypes(userGroupsMap[r.accountId] || []) ?? undefined}
                            >
                              <span className="cursor-default">{fmt.hoursBare(r.totalHours || 0)}</span>
                            </LogTypePopover>
                          ) : (
                            <span>{(() => {
                              const dw = summary.days.filter((d: string) => (r.dailyMinutes?.[d] || 0) > 0).length;
                              return fmt.hoursBare(dw > 0 ? (r.totalHours || 0) / dw : 0);
                            })()}</span>
                          )}
                        </td>
                        {summary.days.map((d: string) => {
                          const mins = r.dailyMinutes?.[d] || 0;
                          const hoursNum = mins / 60;
                          return (
                            <td key={d} data-day={d} style={S.dayC()} className={`border-t border-gray-100 p-1 text-xs text-center whitespace-nowrap group-hover:bg-gray-50 ${isWeekend(d) ? 'bg-gray-50/80' : ''}`}>
                              {mins === 0 ? (
                                bucketFilter === 'none' && overDays.has(d) ? (
                                  <span className="block w-full rounded bg-rose-50 text-rose-700 ring-1 ring-inset ring-rose-200" title="Nothing logged">–</span>
                                ) : (
                                  <span className="text-gray-300" aria-hidden>–</span>
                                )
                              ) : (
                                <LogTypePopover
                                  accountId={r.accountId}
                                  date={d}
                                  onLogTypeClick={(logType) => openIssuePanel(r.accountId, d, r.userName, logType)}
                                  designationTypes={getDesignationLogTypes(userGroupsMap[r.accountId] || []) ?? undefined}
                                >
                                  <button
                                    onClick={() => openIssuePanel(r.accountId, d, r.userName)}
                                    className={`hit-area inline-block w-full rounded transition-opacity motion-reduce:transition-none ${hoursNum > 8 ? 'bg-amber-50 text-amber-800 ring-1 ring-amber-300' : hoursNum < 5 ? 'bg-red-50 text-red-700' : 'bg-emerald-50 text-emerald-700'} ${bucketFilter !== 'all' && hoursBucket(hoursNum) !== bucketFilter ? 'opacity-25' : ''}`}
                                    title={hoursNum > 8 ? 'Doubtful (>8h)' : undefined}
                                  >
                                    {fmt.hoursBare(hoursNum)}
                                  </button>
                                </LogTypePopover>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
                  );
                })()}
              </div>
            ) : userState.status === 'error' ? null : (
              <div className="text-sm text-gray-500">No data for selected month.</div>
            )}
          </div>
        )}

        <SlidePanel isOpen={!!personRow} onClose={() => setPersonKey(null)} title={personRow ? `${personRow.userName} · ${fmt.monthShort(month)}` : ''} widthPercent={30} minWidthPx={400}>
          {personRow && summary && (
            <PersonPanel
              row={personRow}
              days={summary.days}
              today={today}
              year={Number(month.split('-')[0])}
              month={Number(month.split('-')[1])}
              onOpenDay={(d) => {
                setPersonKey(null);
                openIssuePanel(personRow.accountId, d, personRow.userName);
              }}
            />
          )}
        </SlidePanel>

        <SlidePanel
          isOpen={issuePanelOpen}
          onClose={() => {
            setIssuePanelOpen(false);
            setActivePanelLogType(null);
            setPanelLogTypeBreakdown([]);
            setCurrentAccountId('');
            setSelectedDate('');
            collapseAllWorklogDetails();
          }}
          title={issuePanelTitle}
          widthPercent={30}
        >
          <div role="presentation" onClick={(e) => e.stopPropagation()}>
          {/* Log type pills */}
          {panelLogTypeBreakdown.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-3">
              {panelLogTypeBreakdown.map(item => (
                <button
                  key={item.log_type}
                  type="button"
                  onClick={() => handlePillClick(item.log_type)}
                  className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[0.6875rem] font-medium transition-all ${
                    tagColour(item.log_type)
                  } ${activePanelLogType === item.log_type ? 'ring-2 ring-offset-1 ring-blue-400' : 'opacity-75 hover:opacity-100'}`}
                >
                  <span>{item.log_type === '[]' ? '[]' : `[${item.log_type}]`}</span>
                  <span> - </span>
                  <span className="font-bold">{fmt.hours(item.hours)}</span>
                </button>
              ))}
            </div>
          )}
          {issuePanelLoading ? (
            <div className="text-xs text-secondary-500">Loading…</div>
          ) : issuePanelError ? (
            <div role="alert" className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
              The issues could not be loaded: {issuePanelError}
            </div>
          ) : (
            <div>
              <div className="mb-2 text-xs text-secondary-700">
                <span>Total Logged: {fmt.hours(issuePanelTotals.seconds / 3600)}</span>
                <span className="ml-3">Worklogs: {issuePanelTotals.count}</span>
              </div>
              <div className="divide-y divide-secondary-100">
                {issuePanelIssues.length ? issuePanelIssues.map((it: any, idx: number) => (
                  <div key={(it.issue_id || it.issue_key) + '_' + idx} className="py-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <div className="flex-shrink-0 w-6 h-6 bg-primary-100 text-primary-700 rounded-full flex items-center justify-center text-xs font-medium">
                          {idx + 1}
                        </div>
                        <a
                          href={`https://saral-tracker.atlassian.net/browse/${it.issue_key}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm font-medium text-secondary-900 hover:text-primary-600 hover:underline"
                        >
                          {it.issue_key}
                        </a>
                      </div>
                      <div className="text-[0.6875rem] text-secondary-500">{it.project_key}</div>
                    </div>
                    <div className="text-xs text-secondary-700 whitespace-normal break-words">{it.issue_name || it.issue_summary}</div>
                    <div className="mt-1 flex items-center gap-2 text-[0.6875rem]">
                      <span className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-700">{it.issue_type || 'Issue'}</span>
                      {it.priority && <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">{it.priority}</span>}
                      {it.status && <span className={`px-1.5 py-0.5 rounded ring-1 ring-inset ${statusStyle(it.status).badge}`}>{it.status}</span>}
                    </div>
                    <div className="mt-1 text-[0.6875rem] text-secondary-700">
                      {it.worklogcount > 0 ? (
                        <>
                          <button 
                            onClick={() => toggleWorklogDetails(it.issue_id)}
                            className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                          >
                            Total Worklog: {fmt.hours(Number(it.totalseconds || 0) / 3600)}
                          </button>
                          <button 
                            onClick={() => toggleWorklogDetails(it.issue_id)}
                            className="ml-3 text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                          >
                            Worklog Count: {it.worklogcount || 0}
                          </button>
                        </>
                      ) : (
                        <>
                          <span className="text-gray-500">
                            Total Worklog: {fmt.hours(Number(it.totalseconds || 0) / 3600)}
                          </span>
                          <span className="ml-3 text-gray-500">
                            Worklog Count: 0
                          </span>
                        </>
                      )}
                    </div>
                    
                    {/* Worklog Details - Show below issue when expanded */}
                    <div 
                      className={`overflow-hidden transition-all duration-300 ease-in-out ${
                        expandedIssues.has(it.issue_id) 
                          ? 'max-h-[1000px] opacity-100' 
                          : 'max-h-0 opacity-0'
                      }`}
                    >
                      <div 
                        role="presentation"
                        className="mt-3 ml-4 border-l-2 border-gray-200 pl-3"
                        onClick={(e) => e.stopPropagation()} // Prevent collapse when clicking on worklog details
                      >
                        <div className="text-xs text-gray-600 mb-2 font-medium">
                          Worklog Details:
                        </div>
                        {loadingWorklogs.has(it.issue_id) ? (
                          <div className="text-xs text-gray-500">Loading worklogs...</div>
                        ) : worklogDetails[it.issue_id] && worklogDetails[it.issue_id].length > 0 ? (
                          <div className="space-y-2">
                            {worklogDetails[it.issue_id].map((worklog: any, worklogIndex: number) => (
                              <div key={worklog.worklogId} className="bg-gray-50 rounded p-2 text-xs">
                                <div className="flex items-center justify-between mb-1">
                                  <div className="font-medium text-gray-900">
                                    {worklogIndex + 1}. Worklog ID: {worklog.worklogId}
                                  </div>
                                  <div className="font-semibold text-blue-600">
                                    {fmt.hours(Number(worklog.timeSpentHours))}
                                  </div>
                                </div>
                                {worklog.comment && (
                                  <div className="text-gray-600 mb-1">
                                    <span className="font-medium">Comment:</span> {worklog.comment}
                                  </div>
                                )}
                                {worklog.authorName && (
                                  <div className="text-gray-500">
                                    <span className="font-medium">Author:</span> {worklog.authorName}
                                  </div>
                                )}
                                {worklog.started && (
                                  <div className="text-gray-500">
                                    <span className="font-medium">Started:</span> {fmt.dateTime(worklog.started)}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-xs text-gray-500">No worklog details available</div>
                        )}
                      </div>
                    </div>
                  </div>
                )) : (
                  <div className="text-xs text-secondary-500 py-4 text-center">No issues found</div>
                )}
              </div>
            </div>
          )}
          </div>
        </SlidePanel>
        </div>
      </div>
    </div>

    {/* Worklog summary — right-click context menu */}

    {rptCtxMenu && createPortal(
      <div
        ref={rptCtxMenuRef}
        className="fixed bg-white border border-gray-200 rounded-lg shadow-xl py-1 min-w-[200px]"
        style={{ top: rptCtxMenu.y, left: rptCtxMenu.x, zIndex: 1000010 }}
      >
        <button
          onClick={() => { setRptNotifyTarget(rptCtxMenu.user); setRptCtxMenu(null); }}
          className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700 transition-colors"
        >
          <Mail className="h-4 w-4" />
          Send a notification
        </button>
      </div>,
      document.body
    )}

    {/* Notification Modal */}
    {rptNotifyTarget && (
      <NotificationModal
        isOpen={true}
        onClose={() => setRptNotifyTarget(null)}
        recipient={{ display_name: rptNotifyTarget.display_name, email_address: rptNotifyTarget.email_address }}
      />
    )}
    </>
  );
};

export default ReportsPage;
